import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  MenuItem,
  DialogActions,
  Button,
} from '@mui/material';
import {
  TranscriberRequestDto,
  useTranscriberCreateControllerCreateTranscriberMutation,
  useTranscriberEditControllerEditTranscriberMutation,
} from '@shared/services/apiService';
import { t } from 'i18next';
import { toast } from 'react-toastify';
import { CustomErrorToast } from '@shared/components/customErrorToast';
import { TranscriberLanguageEnum, TranscriberProviderEnum } from '../types';
import { useTranscriberContext } from '../TranscriberContext';

const initialTranscriberState: TranscriberRequestDto = {
  provider: TranscriberProviderEnum.Deepgram,
  language: TranscriberLanguageEnum.EN,
  providerDisplayName: '',
};

export const TranscriberCreateDialog: React.FC = () => {
  const { dialogState, transcriber, setTranscriber, closeDialog, refreshTranscribers } =
    useTranscriberContext();

  const [transcriberData, setTranscriberData] =
    useState<TranscriberRequestDto>(initialTranscriberState);

  const [editTranscriber] = useTranscriberEditControllerEditTranscriberMutation();
  const [createTranscriber] = useTranscriberCreateControllerCreateTranscriberMutation();

  useEffect(() => {
    if (!dialogState.createOpen) return;
    if (transcriber) {
      setTranscriberData({
        provider: transcriber.provider,
        language: transcriber.language,
        providerDisplayName: transcriber.providerDisplayName ?? '',
      });
      return;
    }
    setTranscriberData(initialTranscriberState);
  }, [dialogState.createOpen, transcriber]);

  const handleErrorResponse = (error: unknown) => {
    const e: any = error;
    const errorMessage =
      e && e.data && typeof e.data === 'object' && 'message' in e.data
        ? e.data.message
        : e?.message || t('superAdminManageVoice.genericError');
    toast.error((props) => <CustomErrorToast {...props} message={errorMessage} />, {
      closeButton: false,
      autoClose: 5000,
      icon: false,
    });
  };

  const handleSuccessResponse = (message: string) => {
    toast.success(message, {
      closeButton: false,
      autoClose: 5000,
      icon: false,
    });
    setTranscriber(undefined);
    closeDialog('createOpen');
    refreshTranscribers();
  };

  const handleSave = async () => {
    const trimmedDisplayName = (transcriberData.providerDisplayName ?? '').trim();
    const payload: TranscriberRequestDto = {
      provider: transcriberData.provider,
      language: transcriberData.language,
      providerDisplayName: trimmedDisplayName ? trimmedDisplayName : transcriberData.provider,
    };

    try {
      if (transcriber) {
        await editTranscriber({
          id: transcriber.id,
          transcriberRequestDto: payload,
        }).unwrap();
      } else {
        await createTranscriber({
          transcriberRequestDto: payload,
        }).unwrap();
      }
      handleSuccessResponse(
        transcriber ? t('transcriberPage.editSuccess') : t('transcriberPage.createSuccess'),
      );
    } catch (err: unknown) {
      handleErrorResponse(err);
    }
  };

  const handleClose = () => {
    setTranscriber(undefined);
    closeDialog('createOpen');
  };

  return (
    <Dialog open={dialogState.createOpen} onClose={handleClose} fullWidth maxWidth="lg">
      <DialogTitle>
        {transcriber ? t('transcriberPage.editTitle') : t('transcriberPage.createTitle')}
      </DialogTitle>
      <DialogContent>
        <TextField
          margin="dense"
          label={t('transcriberPage.form.providerLabel')}
          fullWidth
          select
          value={transcriberData.provider}
          onChange={(e) =>
            setTranscriberData({
              ...transcriberData,
              provider: e.target.value as TranscriberProviderEnum,
            })
          }>
          {Object.values(TranscriberProviderEnum).map((provider) => (
            <MenuItem key={provider} value={provider}>
              {provider}
            </MenuItem>
          ))}
        </TextField>

        <TextField
          margin="dense"
          label={t('transcriberPage.form.providerDisplayName')}
          fullWidth
          value={transcriberData.providerDisplayName ?? ''}
          onChange={(e) =>
            setTranscriberData({
              ...transcriberData,
              providerDisplayName: e.target.value,
            })
          }
          helperText={t('transcriberPage.form.providerDisplayNameHelper', {
            provider:
              transcriberData.provider || (t('transcriberPage.form.providerLabel') as string),
          })}
        />

        <TextField
          margin="dense"
          label={t('transcriberPage.form.languageLabel')}
          fullWidth
          select
          value={transcriberData.language}
          onChange={(e) =>
            setTranscriberData({
              ...transcriberData,
              language: e.target.value as TranscriberLanguageEnum,
            })
          }>
          {Object.values(TranscriberLanguageEnum).map((language) => (
            <MenuItem key={language} value={language}>
              {language}
            </MenuItem>
          ))}
        </TextField>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} color="primary">
          {t('common.cancel')}
        </Button>
        <Button onClick={handleSave} color="primary">
          {t('common.save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
